import { devicesData, languageData } from '@app/constant';
import { SerializedError } from '@reduxjs/toolkit';
import { BaseQueryApi, FetchArgs, FetchBaseQueryError, fetchBaseQuery } from '@reduxjs/toolkit/query';
import { getErrorMessage, getErrorStatus } from '@state/apiError';
import { type KPIConfig, type LessonType } from '@state/assignmentApi';
import { ChangeEvent } from 'react';
import { formatUtcToLocal, getDurationBetween } from './dateUtils';
import { Language, OperatorType, Type } from './enum';

export const handleChange = <T extends object>(
    formData: T,
    e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement> | ChangeEvent<HTMLSelectElement>,
) => {
    const target = e.target;
    const { name, value, type } = target;

    if (type === 'checkbox') {
        return {
            ...formData,
            [name]: (target as HTMLInputElement).checked,
        };
    } else if (
        type === 'textarea' ||
        type === 'number' ||
        type === 'text' ||
        type === 'email' ||
        type === 'select-one' ||
        type === 'tel' ||
        type === 'password'
    ) {
        return {
            ...formData,
            [name]: value,
        };
    }
    return formData;
};

export const apiBaseQuery = (args: string | FetchArgs, webApi: BaseQueryApi, extraOptions: object) => {
    const apiUrl = import.meta.env.VITE_API_URL;

    const rawBaseQuery = fetchBaseQuery({
        baseUrl: `${apiUrl}`,
        prepareHeaders: (headers) => {
            return headers;
        },
    });

    return rawBaseQuery(args, webApi, extraOptions);
};

export const validateEmail = (email: string) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    return emailRegex.test(email) && !/\.\./.test(email);
};

export const getInitials = (name: string): string => {
    return name
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase())
        .join('');
};
export const decodeHtml = (html: string | null) => {
    if (html) {
        const txt = document.createElement('textarea');
        txt.innerHTML = html;
        return txt.value;
    }

    return null;
};
export const handleError = (err: FetchBaseQueryError | SerializedError) => {
    const msg = getErrorMessage(err);
    const status = getErrorStatus(err);
    if (msg && (status == 400 || status == 401)) {
        return { msg: msg, key: null, redirect: false };
    } else if (status == 500) {
        return { msg: null, key: null, redirect: true };
    } else {
        return { msg: null, key: 'error.serverError', redirect: false };
    }
};
export const formatDuration = (totalSeconds: number): string => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    const pad = (num: number) => String(num).padStart(2, '0');

    return `${pad(minutes)}:${pad(seconds)}`;
};
export const getDevices = (
    types: LessonType[],
): {
    label: string;
    img: string;
}[] => {
    const result: {
        label: string;
        img: string;
    }[] = [];
    types.map((type) => {
        if (type.lesson_type.toLowerCase() === Type._3D.toLowerCase()) {
            result.push(...devicesData[Type._3D]);
        } else if (type.lesson_type.toLowerCase() === Type._360.toLowerCase()) {
            result.push(...devicesData[Type._360]);
        } else {
            result.push(...devicesData[Type._Other]);
        }
    });

    const unique = new Map<string, { label: string; img: string }>();
    for (const item of result) {
        if (!unique.has(item.label)) {
            unique.set(item.label, item);
        }
    }
    return Array.from(unique.values());
};
export const sameNumbers = (a: number[], b: number[]): boolean => {
    if (a.length !== b.length) {
        return false;
    } // quick exit

    const counts = new Map<number, number>();

    for (const n of a) {
        counts.set(n, (counts.get(n) ?? 0) + 1);
    } // tally a[]
    for (const n of b) {
        // subtract b[]
        const c = counts.get(n);
        if (!c) {
            return false;
        } // n not found
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        c === 1 ? counts.delete(n) : counts.set(n, c - 1);
    }
    return counts.size === 0; // all zeroed
};
export const formatDateResult = (language: Language, date?: string | null) => {
    return formatUtcToLocal(date, languageData[language].dateTimeFormat);
};

export const formatDateYearMonthDay = (language: Language, date?: string | null) => {
    return formatUtcToLocal(date, languageData[language].yearMonthDayFormat);
};

export const formatDurationResult = (
    hourPartLabel: string,
    minutePartLabel: string,
    secondPartLabel: string,
    startStr?: string | null,
    endStr?: string | null,
) => {
    if (!startStr || !endStr) {
        return 'N/A';
    }

    const duration = getDurationBetween(startStr, endStr);

    const hourPart = duration.hours > 0 ? `${duration.hours}${hourPartLabel}` : '';
    const minutePart = duration.minutes > 0 ? `${duration.minutes}${minutePartLabel}` : '';
    const secondPart = duration.seconds > 0 ? `${duration.seconds}${secondPartLabel}` : '';

    return [hourPart, minutePart, secondPart].filter(Boolean).join(' ');
};
export const arraysEqual = (a: number[], b: number[]): boolean => {
    return a.length === b.length && a.every((val, index) => val === b[index]);
};

export const getColor = (value: string, config: KPIConfig | null) => {
    const fallback = '#000000';
    const numericValue = parseFloat(value);

    if (Number.isNaN(numericValue)) {
        return fallback;
    }

    // Unified percentage scoring: 100% is always best
    // Use bright colors based on percentage achievement
    if (!config || config.range_rules.length === 0) {
        if (numericValue >= 80) {
            return '#8fec8f'; // Bright green
        }
        if (numericValue >= 50) {
            return '#ffe371'; // Bright yellow
        }
        return '#fa8787'; // Bright red
    }

    // Legacy config-based coloring (for backward compatibility)
    const defaultColor = config.range_rules.find((item) => item.rule === OperatorType.default)?.color ?? fallback;
    const le = config.range_rules.find((item) => item.rule === OperatorType.le);
    const ge = config.range_rules.find((item) => item.rule === OperatorType.ge);

    if (le && numericValue <= le.value) {
        return le.color;
    } else if (ge && numericValue >= ge.value) {
        return ge.color;
    }

    return defaultColor;
};
